import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HeaderComponent } from '@app/public/header/header.component';
import { FooterComponent } from '@app/public/footer/footer.component';

@Component({
  selector: 'app-delete-account',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    HeaderComponent,
    FooterComponent,
  ],
  template: `
    <app-header></app-header>
    <main class="delete-account-page">
      <div class="container">
        <div class="delete-account-content">
          <h1>Supprimer votre compte</h1>

          <div class="warning-box">
            <i class="fas fa-exclamation-triangle"></i>
            <div>
              <h3>Cette action est irréversible</h3>
              <p>
                La suppression de votre compte entraînera la perte définitive de
                toutes vos données, y compris votre historique de commandes et
                vos informations personnelles.
              </p>
            </div>
          </div>

          <section>
            <h2>Avant de supprimer votre compte</h2>
            <p>
              Nous sommes désolés de vous voir partir. Avant de continuer,
              veuillez prendre en compte les points suivants :
            </p>
            <ul>
              <li>
                Toutes vos données personnelles seront définitivement supprimées
              </li>
              <li>
                Votre historique de commandes et de réservations sera perdu
              </li>
              <li>Vous ne pourrez plus accéder à votre compte</li>
              <li>Les réservations en cours seront annulées</li>
            </ul>
            <p>
              Si vous rencontrez des problèmes avec notre service, nous serions
              ravis de vous aider à les résoudre. N'hésitez pas à
              <a href="mailto:contact@zero-waste.pro"
                >contacter notre support</a
              >
              avant de prendre cette décision.
            </p>
          </section>

          <section>
            <h2>Processus de suppression</h2>
            <p>
              Si vous souhaitez toujours supprimer votre compte, voici comment
              procéder :
            </p>

            <div class="steps">
              <div class="step">
                <div class="step-number">1</div>
                <div class="step-content">
                  <h3>Connectez-vous à votre compte</h3>
                  <p>
                    Assurez-vous d'être connecté au compte que vous souhaitez
                    supprimer.
                  </p>
                </div>
              </div>

              <div class="step">
                <div class="step-number">2</div>
                <div class="step-content">
                  <h3>Confirmez votre identité</h3>
                  <p>
                    Pour des raisons de sécurité, nous vous demanderons de
                    confirmer votre mot de passe.
                  </p>
                </div>
              </div>

              <div class="step">
                <div class="step-number">3</div>
                <div class="step-content">
                  <h3>Indiquez la raison de votre départ</h3>
                  <p>
                    Cette étape est facultative mais nous aide à améliorer nos
                    services.
                  </p>
                </div>
              </div>

              <div class="step">
                <div class="step-number">4</div>
                <div class="step-content">
                  <h3>Confirmation finale</h3>
                  <p>
                    Après confirmation, votre compte et vos données seront
                    définitivement supprimés.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section class="delete-form">
            <h2>Formulaire de suppression</h2>
            <p>
              Veuillez remplir ce formulaire pour initier le processus de
              suppression :
            </p>

            <form (submit)="submitDeleteRequest($event)">
              <div class="form-group">
                <label for="email">Adresse e-mail</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  [(ngModel)]="email"
                  required
                />
              </div>

              <div class="form-group">
                <label for="password">Mot de passe</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  [(ngModel)]="password"
                  required
                />
              </div>

              <div class="form-group">
                <label for="reason"
                  >Raison de la suppression (facultatif)</label
                >
                <select id="reason" name="reason" [(ngModel)]="reason">
                  <option value="">Sélectionnez une raison</option>
                  <option value="not-useful">
                    Je n'utilise pas assez le service
                  </option>
                  <option value="privacy">
                    Préoccupations concernant la confidentialité
                  </option>
                  <option value="experience">
                    Mauvaise expérience avec le service
                  </option>
                  <option value="alternative">
                    J'utilise un service alternatif
                  </option>
                  <option value="other">Autre raison</option>
                </select>
              </div>

              <div class="form-group" *ngIf="reason === 'other'">
                <label for="otherReason">Précisez votre raison</label>
                <textarea
                  id="otherReason"
                  name="otherReason"
                  [(ngModel)]="otherReason"
                  rows="3"
                ></textarea>
              </div>

              <div class="form-group checkbox">
                <input
                  type="checkbox"
                  id="confirm"
                  name="confirm"
                  [(ngModel)]="confirmed"
                  required
                />
                <label for="confirm"
                  >Je comprends que cette action est irréversible et que toutes
                  mes données seront définitivement supprimées</label
                >
              </div>

              <div class="form-actions">
                <button
                  type="button"
                  class="btn btn-secondary"
                  [routerLink]="['/']"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  class="btn btn-danger"
                  [disabled]="!confirmed"
                >
                  Supprimer mon compte
                </button>
              </div>
            </form>
          </section>

          <section *ngIf="deleteSubmitted">
            <div class="success-message">
              <i class="fas fa-check-circle"></i>
              <h3>Demande de suppression envoyée</h3>
              <p>
                Nous avons reçu votre demande de suppression de compte. Un
                e-mail de confirmation a été envoyé à l'adresse {{ email }}.
                Veuillez suivre les instructions dans cet e-mail pour finaliser
                la suppression.
              </p>
              <a [routerLink]="['/']" class="btn btn-primary"
                >Retour à l'accueil</a
              >
            </div>
          </section>

          <div class="back-link" *ngIf="!deleteSubmitted">
            <a [routerLink]="['/']">← Retour à l'accueil</a>
          </div>
        </div>
      </div>
    </main>
    <app-footer></app-footer>
  `,
  styles: [
    `
      .delete-account-page {
        padding: 120px 0 60px;
        background-color: #f9f9f9;
      }

      .delete-account-content {
        background-color: var(--white);
        padding: 40px;
        border-radius: 10px;
        box-shadow: var(--shadow);
        max-width: 800px;
        margin: 0 auto;
      }

      h1 {
        color: var(--primary-dark);
        margin-bottom: 20px;
        font-size: 2.5rem;
      }

      .warning-box {
        display: flex;
        align-items: flex-start;
        background-color: #fff3cd;
        border-left: 4px solid #ffc107;
        padding: 20px;
        border-radius: 5px;
        margin-bottom: 30px;
      }

      .warning-box i {
        font-size: 2rem;
        color: #ffc107;
        margin-right: 15px;
      }

      .warning-box h3 {
        color: #856404;
        margin-bottom: 5px;
      }

      .warning-box p {
        color: #856404;
        margin-bottom: 0;
      }

      section {
        margin-bottom: 30px;
      }

      h2 {
        color: var(--primary-color);
        margin-bottom: 15px;
        font-size: 1.5rem;
      }

      h3 {
        color: var(--primary-dark);
        margin-bottom: 10px;
        font-size: 1.2rem;
      }

      p,
      ul {
        margin-bottom: 15px;
        line-height: 1.6;
      }

      ul {
        padding-left: 20px;
      }

      li {
        margin-bottom: 8px;
      }

      a {
        color: var(--primary-color);
        text-decoration: none;
      }

      a:hover {
        text-decoration: underline;
      }

      .steps {
        margin: 30px 0;
      }

      .step {
        display: flex;
        margin-bottom: 20px;
      }

      .step-number {
        background-color: var(--primary-color);
        color: var(--white);
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        margin-right: 15px;
        flex-shrink: 0;
      }

      .step-content {
        flex: 1;
      }

      .delete-form {
        background-color: #f8f9fa;
        padding: 25px;
        border-radius: 8px;
      }

      .form-group {
        margin-bottom: 20px;
      }

      label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
      }

      input[type='email'],
      input[type='password'],
      select,
      textarea {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 5px;
        font-size: 1rem;
      }

      .checkbox {
        display: flex;
        align-items: flex-start;
      }

      .checkbox input {
        margin-top: 5px;
        margin-right: 10px;
        width: auto;
      }

      .checkbox label {
        flex: 1;
      }

      .form-actions {
        display: flex;
        justify-content: space-between;
        margin-top: 30px;
      }

      .btn {
        padding: 10px 20px;
        border-radius: 5px;
        font-weight: 600;
        cursor: pointer;
        border: none;
        transition: all 0.3s ease;
      }

      .btn-secondary {
        background-color: #f8f9fa;
        color: #333;
        border: 1px solid #ddd;
      }

      .btn-danger {
        background-color: #dc3545;
        color: white;
      }

      .btn-danger:disabled {
        background-color: #e9a8ae;
        cursor: not-allowed;
      }

      .btn-primary {
        background-color: var(--primary-color);
        color: white;
      }

      .success-message {
        text-align: center;
        padding: 30px;
        background-color: #d4edda;
        border-radius: 8px;
      }

      .success-message i {
        font-size: 3rem;
        color: #28a745;
        margin-bottom: 15px;
      }

      .success-message h3 {
        color: #155724;
        margin-bottom: 15px;
      }

      .success-message p {
        color: #155724;
        margin-bottom: 20px;
      }

      .back-link {
        margin-top: 30px;
        text-align: center;
      }

      @media (max-width: 768px) {
        .delete-account-page {
          padding: 100px 0 40px;
        }

        .delete-account-content {
          padding: 25px;
        }

        h1 {
          font-size: 2rem;
        }

        .form-actions {
          flex-direction: column;
          gap: 10px;
        }

        .btn {
          width: 100%;
        }
      }
    `,
  ],
})
export class DeleteAccountComponent {
  email: string = '';
  password: string = '';
  reason: string = '';
  otherReason: string = '';
  confirmed: boolean = false;
  deleteSubmitted: boolean = false;

  submitDeleteRequest(event: Event) {
    event.preventDefault();
    if (this.confirmed) {
      // In a real application, this would send a request to the server
      console.log('Delete account request submitted', {
        email: this.email,
        reason: this.reason === 'other' ? this.otherReason : this.reason,
      });
      this.deleteSubmitted = true;
    }
  }
}
